import { useQuery } from '@tanstack/react-query';
import { getLocations, getLocationBySlug, getLocationById, getFeaturedLocations } from '@/api/locations';
import { CustomerLocationDto } from '@/types';

/**
 * Hook to fetch all customer-facing locations
 */
export const useLocations = () => {
    return useQuery<CustomerLocationDto[], Error>({
        queryKey: ['locations'],
        queryFn: getLocations,
        staleTime: 1000 * 60 * 60, // 1 hour
    });
};

/**
 * Hook to fetch featured locations
 */
export const useFeaturedLocations = () => {
    return useQuery<CustomerLocationDto[], Error>({
        queryKey: ['locations', 'featured'],
        queryFn: getFeaturedLocations,
        staleTime: 1000 * 60 * 60, // 1 hour
    });
};

/**
 * Hook to fetch a single location by slug
 * @param slug - The URL-friendly slug of the location
 */
export const useLocationBySlug = (slug: string | undefined) => {
    return useQuery<CustomerLocationDto | null, Error>({
        queryKey: ['locations', 'by-slug', slug],
        queryFn: () => getLocationBySlug(slug!),
        enabled: !!slug,
        staleTime: 1000 * 60 * 60, // 1 hour
    });
};

/**
 * Hook to fetch a single location by ID
 * @param id - The GUID of the location
 */
export const useLocationById = (id: string | undefined) => {
    return useQuery<CustomerLocationDto | null, Error>({
        queryKey: ['locations', id],
        queryFn: () => getLocationById(id!),
        enabled: !!id,
        staleTime: 1000 * 60 * 60, // 1 hour
    });
};
